import { dbService } from '../firebaseInstance';
import { postThread } from '../utils/taskDataFetch';
import { uploadAndMakePublicAvatar, getPublicAvatarUrl, checkAvatarExists } from '../utils/publicAvatarUtils';
import { graphGet } from '../utils/graphProxy';

const getCafeCollectionRef = () =>
  dbService.collection('magazines').doc('monthlyThreads').collection('cafeList');

// 재귀적으로 undefined 필드를 제거하는 유틸리티
const deepRemoveUndefined = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => deepRemoveUndefined(item))
      .filter((v) => v !== undefined);
  }
  if (value && typeof value === 'object') {
    const next = {};
    Object.keys(value).forEach((key) => {
      const v = deepRemoveUndefined(value[key]);
      if (v !== undefined) {
        next[key] = v;
      }
    });
    return next;
  }
  return value === undefined ? undefined : value;
};

export const subscribeCafeList = (onChange, options = {}) => {
  const { includesPending = false, isAdmin = false } = options;
  const ref = getCafeCollectionRef();

  return ref.onSnapshot((snap) => {
    let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Filter based on approval status for non-admin users
    // Note: User registered cafes are now auto-approved
    if (!isAdmin && !includesPending) {
      list = list.filter(cafe =>
        cafe.magazineParticipant ||
        cafe.approvalStatus === 'approved' ||
        !cafe.registrationType || // Legacy cafes without registrationType
        (cafe.registrationType === 'user' && cafe.threadsPostId) // Auto-approved user cafes with threads post
      );
    }

    onChange(list);
  });
};

export const createOrUpdateCafe = async (data) => {
  const { id, name, lat, lng, description, threadsUrl, cafeUrl, address, magazineParticipant = false, userInfo = null, collectionId = undefined, collectionPreview = undefined } = data || {};
  if (!name || typeof lat !== 'number' || typeof lng !== 'number') {
    throw new Error('name, lat, lng are required');
  }
  if (magazineParticipant && !userInfo) {
    throw new Error('userInfo is required when magazineParticipant is true');
  }
  if (magazineParticipant && !collectionId) {
    throw new Error('collectionId is required when magazineParticipant is true');
  }

  const ref = getCafeCollectionRef();
  const now = Date.now();
  let userId = null;
  try { userId = localStorage.getItem('userID'); } catch (_) {}

  if (id && id.trim()) {
    const docRef = ref.doc(id.trim());
    const payload = deepRemoveUndefined({
      id: id.trim(),
      name,
      lat,
      lng,
      description: description || '',
      threadsUrl: threadsUrl || '',
      cafeUrl: cafeUrl || '',
      address: address || '',
      magazineParticipant: !!magazineParticipant,
      userInfo: magazineParticipant ? (userInfo || null) : null,
      collectionId: magazineParticipant ? (collectionId || null) : null,
      collectionPreview: collectionPreview || undefined,
      updatedAt: now,
      updatedBy: userId || null,
    });
    await docRef.set(payload, { merge: true });
    return id.trim();
  }

  const docRef = ref.doc();
  const newId = docRef.id;
  const payload = deepRemoveUndefined({
    id: newId,
    name,
    lat,
    lng,
    description: description || '',
    threadsUrl: threadsUrl || '',
    cafeUrl: cafeUrl || '',
    address: address || '',
    magazineParticipant: !!magazineParticipant,
    userInfo: magazineParticipant ? (userInfo || null) : null,
    collectionId: magazineParticipant ? (collectionId || null) : null,
    collectionPreview: collectionPreview || undefined,
    createdAt: now,
    updatedAt: now,
    createdBy: userId || null,
    updatedBy: userId || null,
  });
  await docRef.set(payload);
  return newId;
};

export const deleteCafe = async (id) => {
  if (!id) return;
  const ref = getCafeCollectionRef();
  await ref.doc(id).delete();
};

// User Registration Functions
export const registerUserCafe = async (data, currentUser) => {
  if (!currentUser || !currentUser.id) {
    throw new Error("사용자 로그인이 필요합니다");
  }

  const { name, address, cafeIntroduction, description, cafeUrl } = data;

  // Validate required fields
  if (!name || !address || !cafeIntroduction) {
    throw new Error("카페 이름, 주소, 소개글은 필수입니다");
  }

  // 글자수 제한 (최대 50자)
  const actualLength = [...cafeIntroduction].length; // 유니코드 정확한 문자 수
  if (actualLength > 50) {
    throw new Error("카페 소개글은 50자를 초과할 수 없습니다");
  }

  // Geocode address
  let lat, lng;
  const coords = await geocodeAddress(address);
  if (coords) {
    lat = coords.lat;
    lng = coords.lng;
  } else {
    throw new Error("주소를 좌표로 변환할 수 없습니다");
  }

  // Check for duplicates
  const duplicates = await checkDuplicateCafe(name, lat, lng);
  if (duplicates) {
    const confirmDuplicate = window.confirm(
      `근처에 비슷한 이름의 카페가 있습니다: ${duplicates[0].name}\n계속 등록하시겠습니까?`
    );
    if (!confirmDuplicate) {
      throw new Error("카페 등록이 취소되었습니다");
    }
  }

  // Post to Threads using original postThread function
  let threadsPostId = null;
  let threadsPostUrl = null;
  let threadsPermalink = null;
  let threadsPostText = cafeIntroduction; // 사용자가 입력한 소개글 그대로 사용
  let postedAt = null;

  try {
    console.log('Posting cafe introduction to Threads...');
    const postResult = await postThread(threadsPostText);
    threadsPostId = postResult.threadsId || postResult.id;
    threadsPostUrl = postResult.threadsUrl || `https://www.threads.net/t/${threadsPostId}`;
    threadsPermalink = postResult.permalink || null;
    postedAt = Date.now();
    console.log('Threads post successful:', { threadsPostId, threadsPostUrl, threadsPermalink });
  } catch (error) {
    console.error('Threads posting failed:', error);
    throw new Error(`스레드 포스팅에 실패했습니다: ${error.message}`);
  }

  const ref = getCafeCollectionRef();
  const docRef = ref.doc();
  const newId = docRef.id;
  const now = Date.now();

  // currentUser 구조 확인 및 username 추출
  console.log('currentUser structure:', currentUser);
  const username = currentUser.profile?.username || currentUser.username || currentUser.user?.profile?.username;
  console.log('extracted username:', username);

  // 먼저 currentUser에 최신 프로필 이미지 정보를 업데이트
  let latestProfileImageUrl = null;
  const token = localStorage.getItem('longLivedToken') || localStorage.getItem('accessToken');

  if (token) {
    try {
      // graph.threads.net 은 브라우저 CORS 미지원 → threadsApiProxy CF 경유
      const response = await graphGet('me', {
        params: { fields: 'threads_profile_picture_url', access_token: token },
      }, { retryOn5xx: false });
      const profileData = response.data;
      if (profileData) {
        latestProfileImageUrl = profileData?.threads_profile_picture_url;

        // currentUser 객체에 프로필 이미지 정보 추가
        if (latestProfileImageUrl) {
          currentUser.profileImage = latestProfileImageUrl;
          currentUser.avatar = latestProfileImageUrl;
          if (!currentUser.profile) {
            currentUser.profile = {};
          }
          currentUser.profile.threads_profile_picture_url = latestProfileImageUrl;
          console.log('✅ Updated currentUser with latest profile image:', latestProfileImageUrl);
        }
      }
    } catch (error) {
      console.warn('Failed to fetch latest profile image:', error);
    }
  }

  // Cache user avatar to Firebase Storage
  let cachedAvatarUrl = null;
  if (latestProfileImageUrl) {
    try {
      console.log('🔄 Caching user avatar to Firebase Storage...');
      cachedAvatarUrl = await uploadAndMakePublicAvatar(currentUser.id, latestProfileImageUrl);
      console.log('✅ Avatar cached successfully:', cachedAvatarUrl);
    } catch (avatarError) {
      console.warn('Failed to cache avatar, using fallback:', avatarError);
    }
  }

  let publicAvatarUrl = null;
  if (cachedAvatarUrl) {
    publicAvatarUrl = getPublicAvatarUrl(currentUser.id, 160);
  } else {
    try {
      const exists = await checkAvatarExists(currentUser.id);
      if (exists) {
        publicAvatarUrl = getPublicAvatarUrl(currentUser.id, 160);
      }
    } catch (error) {
      console.warn('Failed to verify cached avatar existence, skip public URL fallback:', error);
    }
  }

  const finalAvatarUrl = [
    cachedAvatarUrl,
    publicAvatarUrl,
    currentUser.profileImage,
    currentUser.avatar,
    currentUser.profile?.threads_profile_picture_url,
    latestProfileImageUrl
  ].find((url) => typeof url === 'string' && url.length > 0) || '';

  // Generate threadsUrl from user profile
  const threadsUrl = username ? `https://www.threads.net/@${username}` : '';

  const payload = deepRemoveUndefined({
    id: newId,
    name,
    lat,
    lng,
    address,
    description: description || '',
    threadsUrl,
    cafeUrl: cafeUrl || '',
    magazineParticipant: false,
    registrationType: 'user',
    // Admin과 동일한 userInfo 구조 (컬렉션만 제외)
    userInfo: {
      id: currentUser.id,
      profile: {
        id: currentUser.id,
        name: currentUser.displayName || currentUser.profile?.name || currentUser.name,
        username: username,
        threads_profile_picture_url: finalAvatarUrl || currentUser.profile?.threads_profile_picture_url || ''
      },
      profileImage: finalAvatarUrl
    },
    // 내부 관리용 등록자 정보
    registeredBy: {
      userId: currentUser.id,
      username: username,
      profileImage: finalAvatarUrl,
      displayName: currentUser.displayName || currentUser.profile?.name || currentUser.name
    },
    // 사용자 등록 전용 필드들
    cafeIntroduction,
    threadsPostId,
    threadsPostUrl,
    threadsPermalink,
    threadsPostText,
    postedAt,
    // Auto-approved for user registration
    approvalStatus: 'approved',
    createdAt: now,
    updatedAt: now,
    createdBy: currentUser.id,
    updatedBy: currentUser.id
  });

  await docRef.set(payload);
  return newId;
};

// Update user cafe
export const updateUserCafe = async (id, data, currentUser) => {
  const ref = getCafeCollectionRef();
  const doc = await ref.doc(id).get();

  if (!doc.exists) {
    throw new Error("카페를 찾을 수 없습니다");
  }

  const cafe = { id: doc.id, ...doc.data() };

  // Check permission
  if (cafe.createdBy !== currentUser.id && !currentUser.isAdmin) {
    throw new Error("이 카페를 수정할 권한이 없습니다");
  }

  const updateData = {
    updatedAt: Date.now(),
    updatedBy: currentUser.id
  };

  // Update allowed fields
  const allowedFields = ['name', 'address', 'description', 'cafeUrl', 'cafeIntroduction'];
  allowedFields.forEach(field => {
    if (data[field] !== undefined) {
      updateData[field] = data[field];
    }
  });

  // Re-geocode if address changed
  if (data.address && data.address !== cafe.address) {
    const coords = await geocodeAddress(data.address);
    if (coords) {
      updateData.lat = coords.lat;
      updateData.lng = coords.lng;
    }
  }

  await ref.doc(id).update(updateData);
};

// Get user's cafes
export const getUserCafes = async (userId) => {
  const ref = getCafeCollectionRef();
  const snapshot = await ref.where('createdBy', '==', userId).get();

  const cafes = [];
  snapshot.forEach(doc => {
    cafes.push({ id: doc.id, ...doc.data() });
  });

  return cafes;
};

// Admin Functions
export const approveCafe = async (cafeId, adminId) => {
  const ref = getCafeCollectionRef();
  await ref.doc(cafeId).update({
    approvalStatus: 'approved',
    approvedAt: Date.now(),
    approvedBy: adminId,
    updatedAt: Date.now()
  });
};

export const rejectCafe = async (cafeId, adminId, reason = '') => {
  const ref = getCafeCollectionRef();
  await ref.doc(cafeId).update({
    approvalStatus: 'rejected',
    rejectedAt: Date.now(),
    rejectedBy: adminId,
    rejectionReason: reason,
    updatedAt: Date.now()
  });
};

export const getPendingCafes = async () => {
  const ref = getCafeCollectionRef();
  const snapshot = await ref.where('approvalStatus', '==', 'pending').get();

  const cafes = [];
  snapshot.forEach(doc => {
    cafes.push({ id: doc.id, ...doc.data() });
  });

  return cafes.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
};


// Geocoding Helper
export const geocodeAddress = async (address) => {
  try {
    // Try multiple sources for Mapbox token
    const token =
      window.REACT_APP_MAPBOX_TOKEN ||
      window.MAPBOX_TOKEN ||
      localStorage.getItem('MAPBOX_TOKEN') ||
      process.env.REACT_APP_MAPBOX_TOKEN;

    if (!token) {
      console.error("Mapbox token not found");
      return null;
    }

    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?country=KR&language=ko&limit=1&types=address,poi&access_token=${token}`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Geocoding failed: ${response.status}`);
    }

    const data = await response.json();
    const feature = data?.features?.[0];

    if (feature?.center) {
      const [lng, lat] = feature.center;
      return { lat, lng };
    }

    return null;
  } catch (error) {
    console.error("Geocoding error:", error);
    return null;
  }
};

// Check for duplicate cafes
export const checkDuplicateCafe = async (name, lat, lng, radiusMeters = 50) => {
  const cafes = await new Promise((resolve) => {
    const unsub = subscribeCafeList((cafes) => {
      unsub();
      resolve(cafes);
    }, { includesPending: true, isAdmin: true });
  });

  // Haversine formula for distance calculation
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  };

  const duplicates = cafes.filter(cafe => {
    const distance = calculateDistance(lat, lng, cafe.lat, cafe.lng);
    const similarName = cafe.name.toLowerCase().includes(name.toLowerCase()) ||
                       name.toLowerCase().includes(cafe.name.toLowerCase());

    return distance < radiusMeters && similarName;
  });

  return duplicates.length > 0 ? duplicates : null;
};

